import React from 'react';

import './AppHeader.scss';

import i18n from 'i18n';
import _ from 'lodash';
import { ActorMenu } from 'components/ActorMenu';
import {
  AppHeaderProps,
  AppHeaderModel,
  AppHeaderState
} from './AppHeaderModel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars } from '@fortawesome/free-solid-svg-icons';
import appHeaderLogo from 'assets/pmax-logo.png';
import appHeaderLogo2x from 'assets/pmax-logo@2x.png';
import cacaflyLogoSvg from 'assets/cacafly_logo_white.svg';
import { Modal } from 'components/common/Modal/Modal';
import PermissionChecker from 'containers/PermissionChecker/PermissionChecker';
import { hasFuncs } from 'core/permission/PermissionDSL';
import { Permission } from 'core/auth/Permission';
import { LoadingIndicator } from 'components/common/LoadingIndicator';
import { isBeaconAdmin, isBeaconExcecutive } from 'helper/ActorHelper';
import { getAsset } from 'utils/themeConfig';

export class AppHeader extends React.Component<AppHeaderProps, AppHeaderState> {
  menuRef: any;
  languageMenuRef: any;
  handler: number;

  constructor(props: any) {
    super(props);
    this.menuRef = React.createRef();
    this.languageMenuRef = React.createRef();
    this.state = this.props.model.state;
    this.handler = this.props.model.event.add((model) => {
      this.setState(model.state);
    });
  }

  get isBeaconRoles() {
    const actor = this.props.model.actor;
    return isBeaconAdmin(actor) || isBeaconExcecutive(actor);
  }

  logout = () => {
    this.props.model.logout();
  };

  changeLanguage = (language) => {
    this.props.model.showHideLanguageMenu();
    this.props.model.showChangeLanguageAlertModel(language);
  };

  toggle = () => {
    this.props.model.toggle();
  };

  showHideAcountMenu = () => {
    this.props.model.showHideAccoutMenu();
    this.setState(this.props.model.state);
  };

  showHideLanguageMenu = () => {
    this.props.model.showHideLanguageMenu();
    this.setState(this.props.model.state);
  };

  dismiss = (e: any) => {
    if (this.menuRef.current && this.menuRef.current.contains(e.target)) {
      // click inside account menu
    } else if (this.props.model.state.menuShown) {
      this.showHideAcountMenu();
    }
    if (
      this.languageMenuRef.current &&
      this.languageMenuRef.current.contains(e.target)
    ) {
      // click inside language menu
    } else if (this.props.model.state.languageMenuShown) {
      this.showHideLanguageMenu();
    }
  };

  componentDidMount() {
    document.addEventListener('mousedown', this.dismiss, false);
  }

  componentWillUnmount() {
    this.handler && this.props.model.event.remove(this.handler);
    document.removeEventListener('mousedown', this.dismiss);
  }

  accountMenu(model: AppHeaderModel) {
    const classes = model.state.menuShown
      ? 'account-menu'
      : 'account-menu hidden';
    return (
      <ul className={classes}>
        <PermissionChecker permissionAware={hasFuncs(Permission.ACCOUNT_SUDO)}>
          <li className='sudo' onClick={this.props.model.sudo}>
            {i18n.t('appHeader.sudo.title')}
          </li>
        </PermissionChecker>
        <li
          className='changePassword'
          onClick={this.props.model.changePassword}
        >
          {i18n.t('appHeader.changePassword')}
        </li>
        <li className='logout' onClick={this.logout}>
          {i18n.t('appHeader.logout')}
        </li>
      </ul>
    );
  }

  languageMenu(model: AppHeaderModel) {
    if (!model.languages || model.languages.length === 0) {
      return null;
    }
    const classes = model.state.languageMenuShown
      ? 'language-menu'
      : 'language-menu hidden';
    const languageIconSet = {
      en: '🇺🇸',
      'zh-TW': '🇹🇼',
      ja: '🇯🇵'
    };
    return (
      <div className='language-info' ref={this.languageMenuRef}>
        <div className='menu-title' onClick={this.showHideLanguageMenu}>
          <span className='flag'>
            {languageIconSet[model.currentLanguage] || '🏳️'}
          </span>
          <span>{i18n.t(`locales.${model.currentLanguage}`)}</span>
          <span className='arrow' />
        </div>
        <ul className={classes}>
          {model.languages.map((language) => (
            <li
              key={language}
              className='language-option'
              onClick={_.partial(this.changeLanguage, language)}
            >
              <span className='flag'>{languageIconSet[language] || '🏳️'}</span>
              <span>{i18n.t(`locales.${language}`)}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  renderModal(modalData) {
    const model = this.props.model;
    const ModalContent: typeof React.Component = modalData.component;
    return (
      <Modal
        fullScreen={modalData.fullScreen}
        title={modalData.title}
        primaryButton={
          modalData.confirmBtnData && {
            title: modalData.confirmBtnData.title,
            callback: modalData.confirmBtnData.callback
          }
        }
        secondaryButton={{
          title: i18n.t('common.buttons.close'),
          callback: model.hideModal
        }}
        dismiss={model.hideModal}
        animation={false}
      >
        {ModalContent ? (
          <ModalContent {...modalData.componentProps} />
        ) : (
          modalData.message
        )}
      </Modal>
    );
  }

  // logo 來源優先序：domain config 提供的 asset（assets['pmax-logo']）> 原本 bundled logo。
  // 有 override 就一律用（不分 svg/png 分支），並清掉指向 bundled 的 srcSet。
  getLogoImgProps = (): { src: string; srcSet?: string } => {
    const logoOverride = getAsset('pmax-logo');
    const logo2xOverride = getAsset('pmax-logo@2x');
    if (logoOverride) {
      return {
        src: logoOverride,
        srcSet: logo2xOverride ? `${logo2xOverride} 2x` : undefined
      };
    }
    if (this.isBeaconRoles) {
      return { src: cacaflyLogoSvg };
    }
    return { src: appHeaderLogo, srcSet: `${appHeaderLogo2x} 2x` };
  };

  render() {
    const model = this.props.model;
    const actorMenu =
      model.actorMenuModel !== null ? (
        <ActorMenu model={model.actorMenuModel} />
      ) : (
        ''
      );
    const email = model.account !== null ? model.account.email : '';
    return (
      <div className='app-header'>
        {model.state.loading && <LoadingIndicator />}
        <a className='logo' href='/'>
          <img {...this.getLogoImgProps()} alt='logo' />
        </a>
        {model.account !== null && (
          <div className='toggle' onClick={this.toggle}>
            <FontAwesomeIcon icon={faBars} />
          </div>
        )}
        {actorMenu}
        {this.state.modalData && this.renderModal(model.state.modalData)}
        {model.account !== null && this.languageMenu(model)}
        {model.account !== null && (
          <div className='account-info' ref={this.menuRef}>
            <div className='menu-title' onClick={this.showHideAcountMenu}>
              <span>{email}</span>
              <span className='arrow' />
            </div>
            {this.accountMenu(model)}
          </div>
        )}
      </div>
    );
  }
}
